import type {
  Claim,
  CLAIM_EXPORT_TYPE,
  Feedback,
  UpdateFMIFields,
} from '@/lib/data/domains/claim';
import type { Drawing } from '@/lib/data/domains/takeoff-summary';
import {
  type CRUDResponse,
  type CRUDService,
  type EntityFilters,
  getRestClient,
} from '@/lib/data/shared';
import { restService } from '@/lib/utils';

export type ClaimCRUDService = CRUDService<Claim> & {
  saveProgress: ({
    item,
    requestInit,
  }: {
    item: Partial<Claim>;
    requestInit?: RequestInit;
  }) => Promise<CRUDResponse<Claim>>;
  send: ({
    item,
    requestInit,
  }: {
    item: Partial<Claim>;
    requestInit?: RequestInit;
  }) => Promise<CRUDResponse<Claim>>;
  submit: ({
    item,
    requestInit,
  }: {
    item: Partial<Claim>;
    requestInit?: RequestInit;
  }) => Promise<CRUDResponse<Claim>>;
  edit: ({
    item,
    requestInit,
  }: {
    item: Partial<Claim>;
    requestInit?: RequestInit;
  }) => Promise<CRUDResponse<Drawing>>;
  accept: ({
    id,
    requestInit,
  }: {
    id: string;
    requestInit?: RequestInit;
  }) => Promise<CRUDResponse<Claim>>;
  preApprove: ({
    id,
    requestInit,
  }: {
    id: string;
    requestInit?: RequestInit;
  }) => Promise<CRUDResponse<Claim>>;
  askForReview: ({
    id,
    requestInit,
  }: {
    id: string;
    requestInit?: RequestInit;
  }) => Promise<CRUDResponse<Claim>>;
  decline: ({
    id,
    reason,
    requestInit,
  }: {
    id: string;
    reason: string;
    requestInit?: RequestInit;
  }) => Promise<CRUDResponse<Claim>>;
  requestMoreInfo: ({
    id,
    reason,
    requestInit,
  }: {
    id: string;
    reason: string;
    requestInit?: RequestInit;
  }) => Promise<CRUDResponse<Claim>>;
  dispatchToGlazier: ({
    id,
    requestInit,
  }: {
    id: string;
    requestInit?: RequestInit;
  }) => Promise<CRUDResponse<Claim>>;
  feedback: ({
    id,
    feedback,
    requestInit,
  }: {
    id: string;
    feedback: Feedback;
    requestInit?: RequestInit;
  }) => Promise<CRUDResponse<Claim>>;
  saveD365OrderNumber: ({
    id,
    d365OrderNumber,
    requestInit,
  }: {
    id: string;
    d365OrderNumber: string;
    requestInit?: RequestInit;
  }) => Promise<CRUDResponse<Drawing>>;
  saveInternalNotes: ({
    id,
    internalNotes,
    requestInit,
  }: {
    id: string;
    internalNotes: string;
    requestInit?: RequestInit;
  }) => Promise<CRUDResponse<Drawing>>;
  updateFMIFields: ({
    id,
    data,
    requestInit,
  }: {
    id: string;
    data: UpdateFMIFields;
    requestInit?: RequestInit;
  }) => Promise<CRUDResponse<Drawing>>;
  saveFabricatorNotesToFmi: ({
    id,
    internalNotes,
    requestInit,
  }: {
    id: string;
    internalNotes: string;
    requestInit?: RequestInit;
  }) => Promise<CRUDResponse<Drawing>>;
  fetchDrawing: ({
    drawingId,
    requestInit,
  }: {
    drawingId: string;
    requestInit?: RequestInit;
  }) => Promise<CRUDResponse<Drawing>>;
  saveDrawing: ({
    drawing,
    requestInit,
  }: {
    drawing: Drawing;
    requestInit?: RequestInit;
  }) => Promise<CRUDResponse<Drawing>>;
  lockCustomerForm: ({
    id,
    requestInit,
  }: {
    id: string;
    requestInit?: RequestInit;
  }) => Promise<CRUDResponse<Claim>>;
  exportClaims: ({
    claimIds,
    filters,
    exportType,
    requestInit,
  }: {
    claimIds?: string[];
    filters?: EntityFilters;
    exportType?: CLAIM_EXPORT_TYPE;
    requestInit?: RequestInit;
  }) => Promise<CRUDResponse<boolean>>;
};

export const claimRestStorage: ClaimCRUDService = {
  ...getRestClient<Claim>('claim'),
  saveProgress: async ({ item, requestInit }) =>
    restService.request({
      url: `/claim/save-progress`,
      method: 'POST',
      body: item,
      requestInit,
    }),
  send: async ({ item, requestInit }) =>
    restService.request({
      url: `/claim/send`,
      method: 'POST',
      body: item,
      requestInit,
    }),
  submit: async ({ item, requestInit }) =>
    restService.request({
      url: `/claim/${item.id}/submit`,
      method: 'PUT',
      body: item,
      requestInit,
    }),
  edit: async ({ item, requestInit }) =>
    restService.request({
      url: `/claim/${item.id}/update`,
      method: 'PUT',
      body: item,
      requestInit,
    }),
  accept: async ({ id, requestInit }) =>
    restService.request({
      url: `/claim/${id}/accept`,
      method: 'PUT',
      requestInit,
    }),
  preApprove: async ({ id, requestInit }) =>
    restService.request({
      url: `/claim/${id}/pre-approve`,
      method: 'PUT',
      requestInit,
    }),
  askForReview: async ({ id, requestInit }) =>
    restService.request({
      url: `/claim/${id}/ask-for-review`,
      method: 'PUT',
      requestInit,
    }),
  decline: async ({ id, reason, requestInit }) =>
    restService.request({
      url: `/claim/${id}/decline`,
      method: 'PUT',
      body: { reason },
      requestInit,
    }),
  requestMoreInfo: async ({ id, reason, requestInit }) =>
    restService.request({
      url: `/claim/${id}/request-additional-information`,
      method: 'PUT',
      body: { reason },
      requestInit,
    }),
  dispatchToGlazier: async ({ id, requestInit }) =>
    restService.request({
      url: `/claim/${id}/dispatch-to-glazier`,
      method: 'PUT',
      requestInit,
    }),
  feedback: async ({ id, feedback, requestInit }) =>
    restService.request({
      url: `/claim/${id}/feedback`,
      method: 'POST',
      body: feedback,
      requestInit,
    }),
  saveD365OrderNumber: async ({ id, d365OrderNumber, requestInit }) =>
    restService.request({
      url: `/claim/${id}/assign-d365-order-number`,
      method: 'PUT',
      body: { id, d365OrderNumber },
      requestInit,
    }),
  saveInternalNotes: async ({ id, internalNotes, requestInit }) =>
    restService.request({
      url: `/claim/${id}/save-internal-notes`,
      method: 'PUT',
      body: { id, internalNotes },
      requestInit,
    }),
  updateFMIFields: async ({ id, data, requestInit }) =>
    restService.request({
      url: `/claim/${id}/update-fmi-fields`,
      method: 'PUT',
      body: data,
      requestInit,
    }),
  saveFabricatorNotesToFmi: async ({ id, internalNotes, requestInit }) =>
    restService.request({
      url: `/claim/${id}/save-fabricator-additional-notes`,
      method: 'PUT',
      body: { id, internalNotes },
      requestInit,
    }),
  fetchDrawing: async ({ drawingId, requestInit }) =>
    restService.request({
      url: `/claim/${drawingId}/get-drawing`,
      requestInit,
    }),
  saveDrawing: async ({ drawing, requestInit }) =>
    restService.request({
      url: `/claim/${drawing.id}/save-drawing`,
      method: 'PUT',
      body: drawing,
      requestInit,
    }),
  lockCustomerForm: async ({ id, requestInit }) =>
    restService.request({
      url: `/claim/${id}/fabricator-on-behalf-of-customer`,
      method: 'PUT',
      requestInit,
    }),
  exportClaims: async ({ claimIds, filters, exportType, requestInit }) =>
    restService.request({
      url: `/claim/export`,
      method: 'POST',
      body: { claimIds, filters, exportType },
      requestInit,
    }),
};
